<script>
import AccountsPayable from '@/controller/AccountsPayable';
import EvolucoesRelatedBlock from "./evolucoes-related-block.vue";
export default {
    name: 'Detail',
    props: {
        contaPagar: {
            type: Object,
            required: true
        }
    },
    components: { EvolucoesRelatedBlock },
    data() {
        return {
            uploading: false,
            uploadError: null,
            uploadSuccess: null,
            selectedFile: null,
            accountsPayable: null,
            currentEmpresa: null,
            toastCounter: 0,
            evolucoes: [],
            evolucoesLoading: false,
            evolucoesError: null
        };
    },
    created() {
        this.accountsPayable = new AccountsPayable();
        this.currentEmpresa = JSON.parse(localStorage.getItem('currentEmpresa'));
        if (!Array.isArray(this.contaPagar.arquivos)) {
            this.$set(this.contaPagar, 'arquivos', []);
        }

        // Carrega evoluções relacionadas ao conta_pagar_id
        this.loadEvolucoes();
    },
    methods: {
        onPickFile(e) {
            this.selectedFile = e.target.files?.[0] || null;
        },
        async uploadFile() {
            if (!this.selectedFile) return;
            this.uploading = true;
            this.uploadError = null;
            this.uploadSuccess = null;
            try {
                const res = await this.accountsPayable.uploadArquivo(
                    this.contaPagar.id,
                    this.selectedFile,
                    this.currentEmpresa?.id
                );
                if (!res.success) {
                    this.uploadError = res.message || 'Falha ao enviar arquivo';
                    this.toast('danger', this.uploadError);
                } else {
                    const arquivo = res.data;
                    if (arquivo) {
                        if (!Array.isArray(this.contaPagar.arquivos)) {
                            this.$set(this.contaPagar, 'arquivos', []);
                        }
                        this.contaPagar.arquivos.push(arquivo);
                    }
                    this.uploadSuccess = res.message || 'Arquivo enviado com sucesso.';
                    this.toast('success', this.uploadSuccess);
                    this.selectedFile = null;
                    this.$refs.fileInput.value = '';
                }
            } catch (err) {
                this.uploadError = 'Erro ao enviar arquivo';
                this.toast('danger', this.uploadError);
            } finally {
                this.uploading = false;
            }
        },
        async loadEvolucoes() {
            if (!this.contaPagar?.id) return;

            this.evolucoesLoading = true;
            this.evolucoesError = null;

            try {
                const res = await this.accountsPayable.getEvolucoesByContaPagar(
                    this.contaPagar.id,
                    this.currentEmpresa?.id
                );

                if (res?.success) {
                    this.evolucoes = res.data?.evolucoes || [];
                } else {
                    this.evolucoesError = res?.message || 'Falha ao carregar evoluções.';
                    this.evolucoes = [];
                }
            } catch (e) {
                this.evolucoesError = 'Falha ao carregar evoluções.';
                this.evolucoes = [];
            } finally {
                this.evolucoesLoading = false;
            }
        },
        toast(variant, message) {
            if (this.$bvToast && typeof this.$bvToast.toast === 'function') {
                this.toastCounter += 1;
                this.$bvToast.toast(message, {
                    title: 'Contas a Pagar',
                    variant,
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
            }
        },
        async removeArquivo(arquivo) {
            if (!arquivo || !arquivo.id) {
                return;
            }
            try {
                const confirmar = await this.$bvModal?.msgBoxConfirm('Deseja realmente excluir este arquivo?', {
                    title: 'Confirmar exclusão',
                    okVariant: 'danger',
                    okTitle: 'Excluir',
                    cancelTitle: 'Cancelar',
                    centered: true
                });

                if (confirmar === false) return;

                this.uploading = true;
                const res = await this.accountsPayable.deleteArquivo(
                    this.contaPagar.id,
                    arquivo.id,
                    this.currentEmpresa?.id
                );

                if (!res.success) {
                    this.toast('danger', res.message || 'Falha ao excluir arquivo');
                    return;
                }

                const idx = this.contaPagar.arquivos.findIndex(a => a.id === arquivo.id);
                if (idx >= 0) {
                    this.contaPagar.arquivos.splice(idx, 1);
                }
                this.toast('success', res.message || 'Arquivo excluído com sucesso');
            } catch (error) {
                this.toast('danger', 'Erro ao excluir arquivo');
            } finally {
                this.uploading = false;
            }
        },
        formatArquivoDate(date) {
            if (!date) return '';
            return typeof this.formatDateTimeBR === 'function' ? this.formatDateTimeBR(date) : date;
        },
    }
}
</script>
<template>
    <div id="conta-pagar">
        <!-- Bloco: Identificação e situação -->
        <div class="mb-3">
            <div class="d-flex justify-content-between align-items-start">
                <div>
                    <div class="text-muted small">Código</div>
                    <div class="font-weight-bold">{{ contaPagar.id }}</div>
                    <div class="mt-1">
                        <span class="text-muted small">Descrição</span>
                        <div class="font-weight-semibold text-truncate" style="max-width: 520px;">
                            {{ contaPagar.descricao || '-' }}
                        </div>
                    </div>
                </div>
                <div class="text-right">
                    <div class="text-muted small">Situação</div>
                    <b-badge :variant="(contaPagar.status === 0) ? 'danger' : 'success'" class="mt-1">
                        {{ (contaPagar.status === 0) ? 'Pendente' : 'Pago' }}
                    </b-badge>
                </div>
            </div>
        </div>

        <!-- Bloco: Informações da conta -->
        <div class="mb-3">
            <div class="text-muted small mb-2">Informações da conta</div>
            <div class="row">
                <div class="col-md-6 mb-2">
                    <div class="text-muted small">Nº documento</div>
                    <div class="font-weight-semibold">{{ contaPagar.numero_documento || '-' }}</div>
                </div>
                <div class="col-md-6 mb-2">
                    <div class="text-muted small">Pago com</div>
                    <div class="font-weight-semibold">
                        {{ (contaPagar.conta_debito) ? contaPagar.conta_debito.descricao : 'Não informado' }}
                    </div>
                </div>
                <div class="col-md-6 mb-2">
                    <div class="text-muted small">Categoria Despesa</div>
                    <div class="font-weight-semibold">
                        {{ (contaPagar.conta_crebito) ? contaPagar.conta_crebito.descricao : 'Não informado' }}
                    </div>
                </div>
                <div class="col-md-6 mb-2">
                    <div class="text-muted small">Profissional</div>
                    <div class="font-weight-semibold">{{ contaPagar.professional?.name || 'Geral' }}</div>
                </div>
                <div class="col-md-6 mb-2">
                    <div class="text-muted small">Valor</div>
                    <div class="font-weight-semibold text-danger">
                        R$ {{ formatterFloatBR(contaPagar.valor_pagar) }}
                    </div>
                </div>
                <div class="col-md-6 mb-2">
                    <div class="text-muted small">Vencimento</div>
                    <div>
                        <b-badge variant="danger">{{ formatDateBR(contaPagar.vencimento) }}</b-badge>
                    </div>
                </div>
                <div class="col-md-6 mb-2">
                    <div class="text-muted small">Emissão</div>
                    <div class="font-weight-semibold">{{ formatDateBR(contaPagar.data_emissao) }}</div>
                </div>
                <div class="col-md-12" v-if="contaPagar.status === 1">
                    <div class="text-muted small">Data de pagamento</div>
                    <div class="font-weight-semibold">
                        {{ (contaPagar.data_pagamento) ? formatDateBR(contaPagar.data_pagamento) : '-' }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Bloco: Datas / auditoria -->
        <div class="mb-3">
            <div class="text-muted small mb-2">Auditoria</div>
            <div class="row">
                <div class="col-md-6 mb-2">
                    <div class="text-muted small">Criado</div>
                    <div class="font-weight-semibold">{{ formatDateTimeBR(contaPagar.created_at) }}</div>
                </div>
                <div class="col-md-6 mb-2">
                    <div class="text-muted small">Última alteração</div>
                    <div class="font-weight-semibold">{{ formatDateTimeBR(contaPagar.updated_at) }}</div>
                </div>
            </div>
        </div>

        <hr class="my-4">

        <!-- Bloco: Evoluções (recurrences) -->
        <EvolucoesRelatedBlock
            :evolucoes="evolucoes"
            :evolucoesLoading="evolucoesLoading"
            :evolucoesError="evolucoesError"
        />

        <hr class="my-4">

        <!-- Bloco: Anexos -->
        <div class="mt-2">
            <div class="text-muted small mb-2">Anexar arquivo</div>
            <div class="d-flex align-items-center">
                <input type="file" @change="onPickFile" ref="fileInput" />
                <button class="btn btn-sm btn-primary ml-2" :disabled="!selectedFile || uploading" @click="uploadFile">
                    {{ uploading ? 'Enviando...' : 'Enviar' }}
                </button>
            </div>
            <div v-if="uploadError" class="text-danger mt-1">{{ uploadError }}</div>
            <div v-if="uploadSuccess" class="text-success mt-1">{{ uploadSuccess }}</div>
        </div>

        <div class="mt-3">
            <div class="text-muted small mb-2">Arquivos anexados</div>
            <div v-if="!contaPagar.arquivos || contaPagar.arquivos.length === 0" class="text-muted">
                Nenhum arquivo anexado.
            </div>
            <ul v-else class="list-group">
                <li class="list-group-item d-flex justify-content-between align-items-center" v-for="arquivo in contaPagar.arquivos" :key="arquivo.id">
                    <div>
                        <strong>{{ arquivo.nome }}</strong>
                        <div class="small text-muted">{{ formatArquivoDate(arquivo.created_at) }}</div>
                    </div>
                    <div class="d-flex align-items-center">
                        <a v-if="arquivo.url" :href="arquivo.url" target="_blank" rel="noopener" class="btn btn-link btn-sm" :title="'Baixar arquivo'">
                            <i class="ri-download-cloud-fill ml-2 text-primary"></i>
                        </a>
                        <button type="button" class="btn btn-link btn-sm text-danger" :disabled="uploading" @click="removeArquivo(arquivo)" :title="'Excluir arquivo'">
                            <i class="ri-delete-bin-5-fill"></i>
                        </button>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>
